import { useState, useEffect, useRef, useCallback } from 'react';
import { SignalingClient } from '@/lib/webrtc/signaling';
import { PeerSession } from '@/lib/webrtc/peer';
import { acquireLocalMedia, stopStream } from '@/lib/webrtc/media';
import { createLogger } from '@/lib/webrtc/logger';
import { fetchIceServers, FALLBACK_ICE_SERVERS } from '@/lib/webrtc/iceServers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * useVideoChat — the single orchestrator for a chat session.
 *
 * Phases: initializing → searching → connecting → connected | peer_left | media_error
 *
 * Reveal/Hide are pure UI+signaling state: they never touch the PeerConnection,
 * tracks, or SDP. Video always streams; the receiver blurs it until revealed.
 */
export function useVideoChat(session) {
  const log = useRef(createLogger('chat')).current;

  const [phase, setPhase] = useState('initializing');
  const [localRevealed, setLocalRevealed] = useState(false);
  const [remoteRevealed, setRemoteRevealed] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [needsAudioUnlock, setNeedsAudioUnlock] = useState(false);
  const [peerSessionId, setPeerSessionId] = useState(null);
  const [incomingReactions, setIncomingReactions] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);

  const phaseRef = useRef('initializing');
  const signalingRef = useRef(null);
  const peerRef = useRef(null);
  const localStreamRef = useRef(null);
  const peerSocketIdRef = useRef(null);
  const roomIdRef = useRef(null);
  const pendingSignalsRef = useRef([]);
  const initializedRef = useRef(false);
  // Cached ICE server config (STUN + short-lived Cloudflare TURN if available).
  // Refreshed proactively before its TTL expires so every PeerConnection is
  // constructed with valid credentials during the single WebRTC negotiation.
  const iceServersRef = useRef(FALLBACK_ICE_SERVERS);
  const iceRefreshTimerRef = useRef(null);

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);

  const updatePhase = useCallback((next) => {
    phaseRef.current = next;
    setPhase(next);
    log('phase', { phase: next });
  }, [log]);

  const attachRemoteStream = useCallback((stream) => {
    const el = remoteVideoRef.current;
    if (!el) return;
    if (el.srcObject !== stream) {
      el.srcObject = stream;
      log('remote-stream-attached', { tracks: stream.getTracks().map((t) => t.kind) });
    }
    el.play()
      .then(() => {
        setNeedsAudioUnlock(false);
        log('remote-media-playing');
      })
      .catch((err) => {
        log('autoplay-blocked', { error: err.name });
        setNeedsAudioUnlock(true);
      });
  }, [log]);

  const unlockAudio = useCallback(() => {
    const el = remoteVideoRef.current;
    if (!el) return;
    el.play()
      .then(() => {
        setNeedsAudioUnlock(false);
        log('audio-unlocked-by-user');
      })
      .catch((err) => log('audio-unlock-failed', { error: err.message }));
  }, [log]);

  const closePeer = useCallback(() => {
    if (peerRef.current) {
      peerRef.current.close();
      peerRef.current = null;
    }
    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = null;
    }
    pendingSignalsRef.current = [];
    peerSocketIdRef.current = null;
    roomIdRef.current = null;
  }, []);

  const resetCallState = useCallback(() => {
    setLocalRevealed(false);
    setRemoteRevealed(false);
    setNeedsAudioUnlock(false);
    setPeerSessionId(null);
  }, []);

  const handlePeerGone = useCallback(() => {
    if (!peerRef.current && phaseRef.current !== 'connecting' && phaseRef.current !== 'connected') return;
    log('peer-gone');
    closePeer();
    resetCallState();
    updatePhase('peer_left');
  }, [closePeer, resetCallState, updatePhase, log]);

  const createPeer = useCallback((data) => {
    if (peerRef.current) {
      log('duplicate-match-ignored', { roomId: data.room_id });
      return;
    }
    roomIdRef.current = data.room_id;
    peerSocketIdRef.current = data.peer_socket_id;
    setPeerSessionId(data.peer_session_id);
    setLocalRevealed(false);
    setRemoteRevealed(false);
    updatePhase('connecting');
    log('match-found', { roomId: data.room_id, role: data.role });

    const peer = new PeerSession({
      role: data.role,
      localStream: localStreamRef.current,
      iceServers: iceServersRef.current,
      sendSignal: (signal) => signalingRef.current?.sendSignal(peerSocketIdRef.current, signal),
      onRemoteStream: attachRemoteStream,
      onConnected: () => updatePhase('connected'),
      onFailed: handlePeerGone,
    });
    peerRef.current = peer;

    const buffered = pendingSignalsRef.current.splice(0);
    buffered.forEach((s) => peer.handleSignal(s));
    peer.start();
  }, [attachRemoteStream, handlePeerGone, updatePhase, log]);

  useEffect(() => {
    if (!session) return;

    let cancelled = false;

    const init = async () => {
      // Pre-fetch ICE servers (short-lived Cloudflare TURN + STUN) BEFORE
      // any RTCPeerConnection is created. This runs in parallel with media
      // acquisition; if it fails or is slow, iceServersRef stays on the
      // STUN fallback and matchmaking is never blocked.
      const primeIce = async () => {
        const result = await fetchIceServers(BACKEND_URL);
        if (cancelled) return;
        iceServersRef.current = result.iceServers;
        log('ice-primed', { turnAvailable: result.turnAvailable, ttl: result.ttl });
        // Refresh a minute before Cloudflare's TTL expires so long-lived
        // sessions (many rematches) always use fresh credentials.
        if (result.ttl && result.ttl > 120) {
          const refreshMs = (result.ttl - 60) * 1000;
          if (iceRefreshTimerRef.current) clearTimeout(iceRefreshTimerRef.current);
          iceRefreshTimerRef.current = setTimeout(() => {
            if (!cancelled) primeIce();
          }, refreshMs);
        }
      };
      primeIce();

      try {
        const stream = await acquireLocalMedia();
        if (cancelled) {
          stopStream(stream);
          return;
        }
        localStreamRef.current = stream;
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }
      } catch (err) {
        log('media-error', { error: err.message });
        updatePhase('media_error');
        return;
      }

      const signaling = new SignalingClient(BACKEND_URL, session.session_id);
      signalingRef.current = signaling;

      signaling.on('peer_reaction', (data) => {
        if (!data?.emoji) return;
        const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        setIncomingReactions((cur) => [...cur, { id, emoji: data.emoji }]);
        setTimeout(() => {
          setIncomingReactions((cur) => cur.filter((r) => r.id !== id));
        }, 4000);
      });

      signaling.on('peer_chat_message', (data) => {
        if (!data?.text) return;
        setChatMessages((cur) => [
          ...cur,
          { id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, from: 'peer', text: String(data.text).slice(0, 300), at: Date.now() },
        ]);
      });

      signaling.on('peer_left', () => {
        setChatMessages([]);
        setIncomingReactions([]);
      });

      signaling.on('ready', () => {
        updatePhase('searching');
        signaling.joinMatchmaking();
      });

      signaling.on('reconnected', () => {
        if (phaseRef.current === 'searching') {
          signaling.joinMatchmaking();
        }
      });

      signaling.on('rejoined', (data) => {
        if (data?.room_id) {
          peerSocketIdRef.current = data.peer_socket_id;
          setRemoteRevealed(!!data.peer_revealed);
          log('room-restored-after-reconnect', { roomId: data.room_id });
        }
      });

      signaling.on('peer_socket_updated', (data) => {
        peerSocketIdRef.current = data.peer_socket_id;
      });

      signaling.on('searching', () => {
        if (phaseRef.current !== 'connecting' && phaseRef.current !== 'connected') {
          updatePhase('searching');
        }
      });

      signaling.on('match_found', createPeer);

      signaling.on('signal', (data) => {
        if (peerRef.current) {
          peerRef.current.handleSignal(data.signal);
        } else {
          pendingSignalsRef.current.push(data.signal);
          log('signal-buffered-before-match', { type: data.signal?.type });
        }
      });

      signaling.on('peer_revealed', () => setRemoteRevealed(true));
      signaling.on('peer_hidden', () => setRemoteRevealed(false));
      signaling.on('peer_left', handlePeerGone);

      signaling.connect();
    };

    init();

    return () => {
      cancelled = true;
      log('unmount-cleanup');
      if (iceRefreshTimerRef.current) {
        clearTimeout(iceRefreshTimerRef.current);
        iceRefreshTimerRef.current = null;
      }
      closePeer();
      stopStream(localStreamRef.current);
      localStreamRef.current = null;
      signalingRef.current?.leaveRoom();
      signalingRef.current?.disconnect();
      signalingRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const revealSelf = useCallback(() => {
    setLocalRevealed(true);
    signalingRef.current?.reveal(roomIdRef.current);
  }, []);

  const hideSelf = useCallback(() => {
    setLocalRevealed(false);
    signalingRef.current?.hide(roomIdRef.current);
  }, []);

  const toggleAudio = useCallback(() => {
    const track = localStreamRef.current?.getAudioTracks()[0];
    if (!track) return;
    track.enabled = !track.enabled;
    setAudioEnabled(track.enabled);
    log('mic-toggled', { enabled: track.enabled });
  }, [log]);

  const toggleVideo = useCallback(() => {
    const track = localStreamRef.current?.getVideoTracks()[0];
    if (!track) return;
    track.enabled = !track.enabled;
    setVideoEnabled(track.enabled);
    log('camera-toggled', { enabled: track.enabled });
  }, [log]);

  const next = useCallback(() => {
    log('next-clicked');
    closePeer();
    signalingRef.current?.leaveRoom();
    resetCallState();
    setChatMessages([]);
    setIncomingReactions([]);
    updatePhase('searching');
    signalingRef.current?.joinMatchmaking();
  }, [closePeer, resetCallState, updatePhase, log]);

  const sendReaction = useCallback((emoji) => {
    signalingRef.current?.sendReaction(emoji);
  }, []);

  const sendChatMessage = useCallback((text) => {
    const trimmed = String(text || '').slice(0, 300).trim();
    if (!trimmed) return;
    signalingRef.current?.sendChatMessage(trimmed);
    setChatMessages((cur) => [
      ...cur,
      { id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, from: 'me', text: trimmed, at: Date.now() },
    ]);
  }, []);

  return {
    phase,
    localRevealed,
    remoteRevealed,
    audioEnabled,
    videoEnabled,
    needsAudioUnlock,
    peerSessionId,
    localVideoRef,
    remoteVideoRef,
    revealSelf,
    hideSelf,
    toggleAudio,
    toggleVideo,
    next,
    unlockAudio,
    incomingReactions,
    chatMessages,
    sendReaction,
    sendChatMessage,
  };
}
